import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Loader2, Sun, Moon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoginMethodsCard } from '@/components/profile/LoginMethodsCard';
import { useAuth } from '@/contexts/AuthContext';
import { queryClient } from '@/lib/queryClient';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const [loggingOut, setLoggingOut] = useState(false);

  const handleSignOut = async () => {
    setLoggingOut(true);

    // Resolve org slug before signing out (session/storage cleared on signOut)
    let targetPath = '/';
    try {
      const savedPath = sessionStorage.getItem('last_slug_path');
      if (savedPath) {
        targetPath = savedPath;
      } else {
        const ctx = sessionStorage.getItem('org_context');
        if (ctx) {
          const parsed = JSON.parse(ctx);
          if (parsed?.org_slug) targetPath = `/${parsed.org_slug}`;
        } else if ((window as any).__BOOTSTRAP_SLUG__) {
          targetPath = `/${(window as any).__BOOTSTRAP_SLUG__}`;
        }
      }
    } catch {
      // Fallback to /
    }

    try {
      await signOut();
    } catch {
      // signOut failed — still navigate away
    }
    queryClient.clear();
    navigate(targetPath, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-6 space-y-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
            <User className="h-6 w-6 text-foreground" />
          </div>
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold tracking-tight truncate">
              {profile?.display_name || 'Min profil'}
            </h2>
          </div>
        </div>

        <LoginMethodsCard />

        <Card className="rounded-3xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDark ? (
                  <Moon className="h-5 w-5 text-muted-foreground shrink-0" />
                ) : (
                  <Sun className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Utseende</p>
                  <p className="text-sm font-medium">{isDark ? 'Mörkt läge' : 'Ljust läge'}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
              >
                {isDark ? 'Ljust läge' : 'Mörkt läge'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Button
          variant="destructive"
          className="w-full"
          size="lg"
          onClick={handleSignOut}
          disabled={loggingOut}
        >
          {loggingOut ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <LogOut className="h-5 w-5" />
              Logga ut
            </>
          )}
        </Button>
      </main>
    </div>
  );
}
